import { gql, useMutation, useQuery } from "@apollo/client";
import { Feather } from "@expo/vector-icons";
import { useFocusEffect } from "@react-navigation/native";
import { useCallback, useState } from "react";
import { Text, View } from "react-native";
import { ActivityIndicator, RadioButton } from "react-native-paper";
import { colors } from "../../../theme";
import { ActionsContainer, LinkButton, ScreenContainer, ScrollView } from "../../components";
import DisplayAddress from "../../components/Address";
import { EmploymentWarningModal } from "../../components/EmploymentWarningModal";
import InactiveStatusModals from "../../components/InactiveStatusModals";
import PersistentSnackbar from "../../components/PersistentSnackbar";
import ConfirmCancelChangeRequestModal from "../../components/UnsavedChangesModal";
import useSnackBar from "../../hooks/useSnackBar";
import { CANCEL_PENDING_CHANGE_REQUEST } from "../AccountScreen/EditScreen";
import PendingChangeRequestsModal from "../AccountScreen/PendingChangesModal";
import {
  EmploymentCannotBeChangedWarningModal,
  WorkingLocationAffectSectionWarningModal,
} from "../AddEmploymentScreen/WarningModals";
import { RootStackScreenProps, WorkingLocation } from "../types";
import { GET_EMPLOYMENTS_QUERY } from "./query";
import RemoveEmploymentModal from "./RemoveEmploymentModal";
import { getEmploymentsQuery } from "./types";

export const editEmploymentTitle = "Employment Details";

interface UpdateEmploymentWorkingLocationMutationResponse {
  updateEmploymentWorkingLocation: {
    success: boolean;
    message: string;
  };
}

interface UpdateEmploymentWorkingLocationMutationVariables {
  employmentId: string;
  workingLocation: WorkingLocation;
}

export const UPDATE_WORKING_LOCATION_MUTATION = gql`
  mutation updateEmploymentWorkingLocation($employmentId: ID!, $workingLocation: CRMEmploymentWorkingLocation!) {
    updateEmploymentWorkingLocation(employmentId: $employmentId, workingLocation: $workingLocation) {
      success
      message
    }
  }
`;

const Divider = () => <View className="border-t border-sky-lighter" />;

const EmploymentEditScreen = ({ route, navigation }: RootStackScreenProps<"EmploymentEdit">) => {
  const [errorMessage, setErrorMessage] = useState("");
  const [isEmploymentCannotBeChangedModalVisible, setIsEmploymentCannotBeChangedModalVisible] = useState(false);
  const [isRemoveEmploymentModalVisible, setIsRemoveEmploymentModalVisible] = useState(false);
  const [showStatusChangeModal, setShowStatusChangeModal] = useState("");
  const [pendingChangeRequestsModalVisible, setPendingChangeRequestsModalVisible] = useState(false);
  const [confirmCancelPendingChangesModal, setConfirmCancelPendingChangesModal] = useState<{
    open: boolean;
    id: string | null;
  }>({
    open: false,
    id: null,
  });
  const showSnackBar = useSnackBar();

  const [showWarningModal, setShowWarningModal] = useState(false);
  const [toConfirmWorkingLocation, setToConfirmWorkingLocation] = useState<WorkingLocation>();

  const employmentId = route.params?.id;
  const { data: employmentsData, loading: loadingEmployments, refetch } = useQuery<getEmploymentsQuery>(GET_EMPLOYMENTS_QUERY);
  const [cancelChangeRequest, { loading: cancelChangeRequestLoading }] = useMutation(CANCEL_PENDING_CHANGE_REQUEST);

  const handleCancelChangeRequest = async (id: string) => {
    const { data } = await cancelChangeRequest({ variables: { id } });
    if (data?.cancelChangeRequest.success) {
      showSnackBar({ message: "Change request cancelled", duration: 3000, icon: "check-circle" });
      refetch();
      setConfirmCancelPendingChangesModal({ open: false, id: null });
    } else {
      const errorMsg = data?.cancelChangeRequest.message || "Unexpected error, please try again.";
      showSnackBar({ message: errorMsg, duration: 3000, icon: "x-circle" });
    }
  };

  const [updateEmploymentWorkingLocation] = useMutation<
    UpdateEmploymentWorkingLocationMutationResponse,
    UpdateEmploymentWorkingLocationMutationVariables
  >(UPDATE_WORKING_LOCATION_MUTATION);

  useFocusEffect(
    useCallback(() => {
      refetch();
    }, [])
  );

  if (loadingEmployments) {
    return <ActivityIndicator className="flex-1 items-center justify-center bg-white" />;
  }

  const hasOnlyOneEmployment = employmentsData?.currentPerson?.employments.length === 1;

  const employmentToEdit = employmentsData?.currentPerson?.employments.find((employment) => employment.id === employmentId);

  const employmentType = employmentToEdit?.isPrimary ? "Primary Employment" : "Secondary Employment";

  const pendingChangeRequests = employmentsData?.currentPerson.pendingChangeRequests?.filter(
    ({ facility }) => facility.id === employmentToEdit?.facility.id
  );

  if (!employmentToEdit) {
    return <Text className="text-regular-normal text-ink-darker m-8 text-center">Sorry, employment not found</Text>;
  }

  const handleWorkingLocationChange = (workingLocation: string) => {
    setToConfirmWorkingLocation(WorkingLocation[workingLocation as keyof typeof WorkingLocation]);
  };

  const onCancelWorkingLocation = () => {
    setToConfirmWorkingLocation(undefined);
  };

  const onConfirmWorkingLocation = async () => {
    setToConfirmWorkingLocation(undefined);

    if (toConfirmWorkingLocation) {
      try {
        const response = await updateEmploymentWorkingLocation({
          variables: { employmentId: employmentToEdit.id, workingLocation: toConfirmWorkingLocation },
        });

        const { success } = response?.data?.updateEmploymentWorkingLocation ?? {};
        if (!success) throw new Error();
        showSnackBar({ message: "Employment Updated", icon: "check-circle", duration: 1500 });
        refetch();
      } catch (error) {
        setErrorMessage((error as Error).message || "Something went wrong");
      }
    }
  };

  return (
    <ScreenContainer navigationTitle={editEmploymentTitle} goBack={() => navigation.goBack()}>
      <EmploymentCannotBeChangedWarningModal
        isModalVisible={isEmploymentCannotBeChangedModalVisible}
        setIsModalVisible={setIsEmploymentCannotBeChangedModalVisible}
      />
      <RemoveEmploymentModal
        visible={isRemoveEmploymentModalVisible}
        onDismiss={() => setIsRemoveEmploymentModalVisible(false)}
        navigation={navigation}
        employmentId={employmentId}
        onRemoveAndChangeStatus={() => setShowWarningModal(true)}
        setShowStatusChangeModal={setShowStatusChangeModal}
      />
      <EmploymentWarningModal
        visible={showWarningModal}
        onDismiss={() => setShowWarningModal(false)}
        title="Unemployed status"
        text="When unemployed, your section assignment and membership options and benefits could be affected."
        onContinue={() => {
          navigation.navigate("EmploymentRemove", { id: employmentId });
          setShowWarningModal(false);
        }}
      />
      <InactiveStatusModals
        showStatusChangeWarningModal={showStatusChangeModal}
        setShowStatusChangeWarningModal={setShowStatusChangeModal}
        onContinue={() => navigation.navigate("EmploymentRemove", { id: employmentId, nextAction: showStatusChangeModal })}
      />
      <PendingChangeRequestsModal
        {...{
          visible: pendingChangeRequestsModalVisible,
          onDismiss: () => setPendingChangeRequestsModalVisible(false),
          pendingChangeRequests: pendingChangeRequests || [],
          refetchRequests: refetch,
          onContinue: (id: string) => {
            setPendingChangeRequestsModalVisible(false);
            setConfirmCancelPendingChangesModal({ open: true, id });
          },
        }}
      />
      <ConfirmCancelChangeRequestModal
        mutationLoading={cancelChangeRequestLoading}
        showUnsavedChangesModal={confirmCancelPendingChangesModal.open}
        setShowUnsavedChangesModal={() => setConfirmCancelPendingChangesModal({ open: false, id: null })}
        discardAndExit={() => {
          setConfirmCancelPendingChangesModal({ open: false, id: null });
          setPendingChangeRequestsModalVisible(false);
        }}
        handleSubmit={() => {
          handleCancelChangeRequest(confirmCancelPendingChangesModal.id!);
        }}
        title="Are you sure you wish to cancel the pending change request?"
        message="Your employment change request will be canceled if you continue."
        buttonLabel="Continue"
        cancelButtonLabel="Go Back"
      />
      <WorkingLocationAffectSectionWarningModal
        isModalVisible={!!toConfirmWorkingLocation}
        onCancel={onCancelWorkingLocation}
        onConfirm={onConfirmWorkingLocation}
      />
      <View className="flex-1">
        <ScrollView contentContainerStyle={{ flexGrow: 1 }}>
          {pendingChangeRequests?.length ? (
            <View className="px-4 mt-1">
              <PersistentSnackbar
                IconComp={() => <Feather name="info" color={colors.blue.DEFAULT} size={24} />}
                text="Pending changes"
                action={() => setPendingChangeRequestsModalVisible(true)}
                borderColor="border-blue-light"
                backgroundColor={colors.blue.lightest}
              />
            </View>
          ) : null}
          <View className="flex-1 justify-between">
            <View>
              <View className="flex-col mt-4 p-6">
                <Text className="text-regular-tight font-700 text-ink-darkest">Facility</Text>
                <View className="mt-4">
                  <Text className="text-regular-normal  text-ink-darker">{employmentToEdit.facility?.name}</Text>
                  {employmentToEdit.facility?.addresses[0] && (
                    <DisplayAddress address={employmentToEdit.facility?.addresses[0]} />
                  )}
                </View>
              </View>
              <Divider />
              <View className="flex-col p-6">
                <View className="flex-row justify-between items-center">
                  <Text className="text-regular-tight font-700 text-ink-darkest mt-4">Job Title</Text>
                  <LinkButton
                    onPress={() => navigation.navigate("UpdateEmployment", { employmentId: employmentId })}
                    testID="edit-job-title-button"
                  >
                    Edit
                  </LinkButton>
                </View>
                <Text className="text-regular-normal  text-ink-darker mt-4">{employmentToEdit.title}</Text>
                <Text className="text-regular-tight font-700 text-ink-darkest mt-6">Job Description</Text>
                <Text className="text-regular-normal  text-ink-darker mt-4">{employmentToEdit.description}</Text>
              </View>
              <Divider />
              <View className="flex-col p-6">
                <View className="flex-row justify-between items-center">
                  <Text className="text-regular-tight font-700 text-ink-darkest">Employment Type</Text>
                  <LinkButton
                    testID="edit-employment-type-button"
                    onPress={() =>
                      hasOnlyOneEmployment
                        ? setIsEmploymentCannotBeChangedModalVisible(true)
                        : navigation.navigate("ChangeEmploymentType")
                    }
                  >
                    Edit
                  </LinkButton>
                </View>
                <Text className="text-regular-normal  text-ink-darker mt-4">{employmentType}</Text>
              </View>
              <Divider />
              <View className="flex-col p-6">
                <View className="flex-row justify-between items-center">
                  <Text className="text-regular-tight font-700 text-ink-darkest">Working Location</Text>
                </View>
                <RadioButton.Group onValueChange={handleWorkingLocationChange} value={employmentToEdit.workingLocation}>
                  <View className="flex flex-row mt-3">
                    <View className="flex flex-row items-center">
                      <RadioButton.Android
                        value={WorkingLocation.ON_SITE}
                        status={employmentToEdit.workingLocation === WorkingLocation.ON_SITE ? "checked" : "unchecked"}
                        uncheckedColor={colors.sky.DEFAULT}
                        testID="working-location-on-site-btn"
                      />
                      <Text className="font-400 text-small-normal text-black mr-6">On-site</Text>
                    </View>

                    <View className="flex flex-row items-center">
                      <RadioButton.Android
                        value={WorkingLocation.REMOTE}
                        status={employmentToEdit.workingLocation === WorkingLocation.REMOTE ? "checked" : "unchecked"}
                        uncheckedColor={colors.sky.DEFAULT}
                        testID="working-location-remote-btn"
                      />
                      <Text className="font-400 text-small-normal text-black">Remote/Regional</Text>
                    </View>
                  </View>
                </RadioButton.Group>
              </View>
            </View>
          </View>
          <ActionsContainer errorMessage={errorMessage} onCloseErrorMessage={setErrorMessage} hideBorder errorAlert>
            <View></View>
          </ActionsContainer>
        </ScrollView>
      </View>
    </ScreenContainer>
  );
};

export default EmploymentEditScreen;
